import {
  Box,
  Flex,
  Label,
  Link,
  Text,
  Tooltip,
  useAppTheme,
} from '@workwhile/ui'
import { ChevronDown, ChevronRight, User } from 'lucide-react'
import { formatCurrency } from 'lib/currency'
import { getLocalTimezone } from 'lib/time'
import {
  AdminChargeItem,
  AdminChargeItemWorkBreak,
} from 'api/admin/charges/getCharges'
import { differenceInMinutes } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import React, { useState } from 'react'
import {
  formatChargesDate,
  formatChargesDateTime,
  formatChargesRelative,
} from '../formatDateTime'
import { Detail, DetailRow } from './Detail'
import {
  formatPayBreakdownMinutes,
  PayBreakdownTooltipContent,
} from 'components/PayBreakdownTooltipContent'

function toNumber(value: string | number): number {
  return typeof value === 'number' ? value : Number(value)
}

function formatOptionalTime(isoTime: string | null, timezone: string) {
  if (isoTime === null) {
    return '—'
  }
  return formatInTimeZone(isoTime, timezone, 'MMM d, h:mm a')
}

function formatClockedDuration(
  startTime: string | null,
  endTime: string | null
) {
  if (startTime === null || endTime === null) {
    return '—'
  }

  const minutes = differenceInMinutes(new Date(endTime), new Date(startTime))
  return formatPayBreakdownMinutes(minutes)
}

/**
 * A break can carry a duration without a clock time (and vice versa), so render
 * only the parts that are actually present rather than padding with dashes.
 */
function formatBreak(
  workBreak: AdminChargeItemWorkBreak,
  timezone: string
): string {
  const parts: string[] = []
  if (workBreak.lunchedAt !== null) {
    parts.push(formatInTimeZone(workBreak.lunchedAt, timezone, 'MMM d, h:mm a'))
  }
  if (workBreak.durationMinutes !== null) {
    parts.push(`${workBreak.durationMinutes}m`)
  }
  return parts.length > 0 ? parts.join(' · ') : '—'
}

type ChargeItemRowProps = {
  item: AdminChargeItem
  shiftId?: number | null
  timezone?: string | null
}

export const ChargeItemRow = ({
  item,
  shiftId,
  timezone,
}: ChargeItemRowProps) => {
  const { colors } = useAppTheme()
  const [isOpen, setIsOpen] = useState(false)
  const work = item.work
  const clockTimezone = timezone ?? getLocalTimezone()
  const amount = toNumber(item.amount)
  const workHref =
    shiftId != null && work != null
      ? `/shifts/${shiftId}#work-${work.id}`
      : null

  return (
    <Box py={2} style={{ borderBottom: `1px solid ${colors.neutrals[100]}` }}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        flexWrap="wrap"
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Collapse line item' : 'Expand line item'}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setIsOpen((prev) => !prev)
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        <Flex alignItems="center" gap={2} flexWrap="wrap" flex={1}>
          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Label variant="none" size="small" inline>
            {item.itemType}
          </Label>
          {work?.workerName != null ? (
            <Flex alignItems="center" gap={1}>
              <User size={12} color={colors.lightText} />
              <Text fontSize={1} fontWeight={600}>
                {work.workerName}
              </Text>
            </Flex>
          ) : null}
          <Text fontSize={1} color="lightText" whiteSpace="wrap">
            {item.description}
          </Text>
        </Flex>
        <Flex alignItems="center" gap={3} style={{ whiteSpace: 'nowrap' }}>
          <Text fontSize={0} color="lightText">
            {String(item.quantity)} ×{' '}
            {formatCurrency(toNumber(item.unitAmount))}
          </Text>
          <Text
            fontSize={1}
            fontWeight={700}
            color={amount < 0 ? 'error' : 'text'}
          >
            {formatCurrency(amount)}
          </Text>
        </Flex>
      </Flex>

      {isOpen ? (
        <Box
          mt={2}
          px={3}
          py={2}
          backgroundColor={colors.neutrals[50]}
          style={{ borderRadius: 4 }}
          onClick={(event) => event.stopPropagation()}
        >
          <DetailRow>
            <Detail label="Line item">#{item.id}</Detail>
            <Detail label="Type">{item.itemType}</Detail>
            <Detail label="Quantity">{String(item.quantity)}</Detail>
            <Detail label="Unit amount">
              {formatCurrency(toNumber(item.unitAmount))}
            </Detail>
            <Detail label="Amount">{formatCurrency(amount)}</Detail>
            <Detail
              label="Created"
              title={formatChargesDateTime(item.createdAt)}
            >
              {formatChargesDate(item.createdAt)} (
              {formatChargesRelative(item.createdAt)})
            </Detail>
          </DetailRow>

          {work != null ? (
            <DetailRow mt={3}>
              <Detail label="Work">
                {workHref != null ? (
                  <Link to={workHref} target="_blank">
                    #{work.id}
                  </Link>
                ) : (
                  `#${work.id}`
                )}
              </Detail>
              <Detail label="Worker">
                {work.workerName ?? 'Unknown'}{' '}
                <Link to={`/user/${work.workerId}`} target="_blank">
                  #{work.workerId}
                </Link>
              </Detail>
              <Detail label="Clock in">
                {formatOptionalTime(work.clocks.startedAt, clockTimezone)}
              </Detail>
              <Detail label="Clock out">
                {formatOptionalTime(work.clocks.completedAt, clockTimezone)}
              </Detail>
              <Detail label="Clocked">
                {formatClockedDuration(
                  work.clocks.startedAt,
                  work.clocks.completedAt
                )}
              </Detail>
              <Detail label="Lunch">
                {formatBreak(work.break, clockTimezone)}
              </Detail>
              {work.payBreakdown != null ? (
                <Detail label="Payable">
                  <Tooltip
                    variant="neutral"
                    content={
                      <PayBreakdownTooltipContent
                        payBreakdown={work.payBreakdown}
                      />
                    }
                  >
                    <Box
                      as="span"
                      style={{
                        display: 'inline-block',
                        borderBottom: `1px dotted ${colors.lightText}`,
                        cursor: 'help',
                      }}
                    >
                      {formatPayBreakdownMinutes(
                        work.payBreakdown.totalWorkDurationMinutes
                      )}
                    </Box>
                  </Tooltip>
                </Detail>
              ) : null}
              {work.w2PayrollFrequency != null ? (
                <Detail label="W2 payroll">{work.w2PayrollFrequency}</Detail>
              ) : null}
            </DetailRow>
          ) : (
            <Text fontSize={1} color="lightText" mt={2}>
              Not tied to a work record.
            </Text>
          )}
        </Box>
      ) : null}
    </Box>
  )
}
